import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { games } from '../data/games';
import GameCard from '../components/GameCard';

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(2deg); }
`;

const Hero = styled.section`
  position: relative;
  padding: 120px 24px 80px;
  overflow: hidden;
  &::before, &::after {
    content: '';
    position: absolute;
    width: 480px;
    height: 480px;
    border-radius: 50%;
    filter: blur(110px);
    opacity: 0.55;
    z-index: 0;
    pointer-events: none;
  }
  &::before {
    background: radial-gradient(circle, #ff3d7f, transparent 70%);
    top: -120px;
    left: -80px;
    animation: ${float} 14s ease-in-out infinite;
  }
  &::after {
    background: radial-gradient(circle, #7c5cff, transparent 70%);
    top: 40px;
    right: -120px;
    animation: ${float} 18s ease-in-out infinite reverse;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
`;

const Eyebrow = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border-radius: 999px;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  font-size: 0.85rem;
  color: var(--text-dim);
  margin-bottom: 28px;
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #44dd88;
    box-shadow: 0 0 12px #44dd88;
  }
`;

const Headline = styled(motion.h1)`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2.6rem, 6.5vw, 5rem);
  line-height: 1.04;
  letter-spacing: -0.03em;
  margin: 0 0 24px;
  font-weight: 700;
  span {
    background: var(--grad-warm);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
`;

const Sub = styled(motion.p)`
  max-width: 640px;
  margin: 0 auto 40px;
  font-size: clamp(1rem, 1.6vw, 1.18rem);
  line-height: 1.65;
  color: var(--text-dim);
`;

const CtaRow = styled(motion.div)`
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
`;

const Primary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 28px;
  border-radius: 999px;
  background: var(--grad-warm);
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  box-shadow: var(--shadow-glow);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 26px 70px -20px rgba(255, 61, 127, 0.65);
  }
`;

const Ghost = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 28px;
  border-radius: 999px;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  color: var(--text);
  font-weight: 600;
  font-size: 1rem;
  transition: background 0.25s ease, border-color 0.25s ease;
  &:hover {
    background: var(--surface-strong);
    border-color: rgba(255, 255, 255, 0.28);
  }
`;

const Section = styled.section`
  padding: 80px 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHead = styled.div`
  text-align: center;
  margin-bottom: 56px;
`;

const Kicker = styled.div`
  font-size: 0.8rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--accent-2);
  font-weight: 600;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.9rem, 4vw, 2.8rem);
  margin: 0 0 16px;
  letter-spacing: -0.02em;
`;

const SectionSub = styled.p`
  color: var(--text-dim);
  max-width: 560px;
  margin: 0 auto;
  font-size: 1.05rem;
  line-height: 1.65;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled(motion.div)`
  position: relative;
  padding: 32px;
  border-radius: 20px;
  background: var(--surface);
  border: 1px solid var(--border);
  overflow: hidden;
  transition: transform 0.3s ease, border-color 0.3s ease;
  &:hover {
    transform: translateY(-4px);
    border-color: var(--border-strong);
  }
`;

const FIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: ${({ $accent }) => $accent};
  display: grid;
  place-items: center;
  color: #fff;
  margin-bottom: 18px;
  box-shadow: 0 12px 30px -10px rgba(0,0,0,0.5);
`;

const FTitle = styled.h3`
  margin: 0 0 8px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.25rem;
`;

const FDesc = styled.p`
  margin: 0;
  color: var(--text-dim);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const GamesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  @media (max-width: 980px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const CtaBox = styled.section`
  position: relative;
  margin: 80px 24px 0;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  border-radius: 32px;
  padding: 64px 32px;
  text-align: center;
  background:
    radial-gradient(800px 300px at 20% 0%, rgba(255, 122, 61, 0.25), transparent 60%),
    radial-gradient(800px 300px at 80% 100%, rgba(124, 92, 255, 0.25), transparent 60%),
    rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border-strong);
  overflow: hidden;
`;

export default function Home() {
  return (
    <>
      <Hero>
        <HeroInner>
          <Eyebrow
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            New release · Crazy Bus Jam is live on Google Play
          </Eyebrow>
          <Headline
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
          >
            Mobile games that <span>pull you in</span> and never let go.
          </Headline>
          <Sub
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            Zero Immersive is a Canadian studio building puzzle, simulator, and
            arcade titles for iOS and Android — designed to feel addictive in
            seconds and rewarding for hours.
          </Sub>
          <CtaRow
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            <Primary href="#games">
              Play our games
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </Primary>
            <Ghost to="/about">
              Get in touch
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Ghost>
          </CtaRow>
        </HeroInner>
      </Hero>

      <Section>
        <SectionHead>
          <Kicker>Why Zero Immersive</Kicker>
          <SectionTitle>Small studio. Big obsession with feel.</SectionTitle>
          <SectionSub>
            We sweat the moment-to-moment of mobile play — controls, pacing,
            satisfying feedback. The kind of polish you notice after one swipe.
          </SectionSub>
        </SectionHead>
        <FeatureGrid>
          <Feature
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
          >
            <FIcon $accent="linear-gradient(135deg, #ff7a3d, #ff3d7f)">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="6" width="20" height="12" rx="3" />
                <line x1="6" y1="11" x2="10" y2="11" />
                <line x1="8" y1="9" x2="8" y2="13" />
                <circle cx="16" cy="11" r="1" />
                <circle cx="18" cy="13" r="1" />
              </svg>
            </FIcon>
            <FTitle>Tight, tactile gameplay</FTitle>
            <FDesc>Every tap, drag, and swipe is tuned for that one-more-round feeling on a phone screen.</FDesc>
          </Feature>
          <Feature
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: 0.08 }}
          >
            <FIcon $accent="linear-gradient(135deg, #7c5cff, #3dd6ff)">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="6" y="2" width="12" height="20" rx="3" />
                <line x1="11" y1="18" x2="13" y2="18" />
              </svg>
            </FIcon>
            <FTitle>Built for iOS & Android</FTitle>
            <FDesc>Cross-platform from day one — native performance on flagship phones and budget devices alike.</FDesc>
          </Feature>
          <Feature
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: 0.16 }}
          >
            <FIcon $accent="linear-gradient(135deg, #3dd6ff, #7c5cff)">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </FIcon>
            <FTitle>Player-led updates</FTitle>
            <FDesc>Reviews and feedback drive our roadmap — we ship updates that fix the things players actually mention.</FDesc>
          </Feature>
        </FeatureGrid>
      </Section>

      <Section id="games">
        <SectionHead>
          <Kicker>Our Games</Kicker>
          <SectionTitle>Three live titles. More on the way.</SectionTitle>
          <SectionSub>
            Tap any card to grab the game on Google Play.
          </SectionSub>
        </SectionHead>
        <GamesGrid>
          {games.map((g, i) => (
            <GameCard key={g.id} game={g} index={i} />
          ))}
        </GamesGrid>
      </Section>

      <CtaBox>
        <Kicker>Let's talk</Kicker>
        <SectionTitle>Got an idea, a partnership, or a bug report?</SectionTitle>
        <SectionSub>
          We read every message. Reach out and we'll get back within a couple of
          business days.
        </SectionSub>
        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Primary as={Link} to="/about">
            Contact the studio
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </Primary>
          <Ghost as="a" href="mailto:accounts@zeroimmersive.com">
            Email us directly
          </Ghost>
        </div>
      </CtaBox>
    </>
  );
}
